'use client';

import React from 'react';
import { X } from 'lucide-react';
import type { ChildEntryType } from '../types';

export interface ConfigurableEntryType {
  value: ChildEntryType;
  label: string;
  icon: string;
}

export interface CaptureButtonConfigModalProps {
  /** 0-based slot index being configured */
  slotIndex: number;
  configurableEntryTypes: ConfigurableEntryType[];
  onSelect: (entryType: ChildEntryType) => void;
  onClose: () => void;
}

/**
 * SmartAutomation capture-button configuration modal.
 * Shown when an unconfigured camera grid button (1–3) is tapped, letting the
 * user assign an expense category to that slot. Selection is persisted to
 * localStorage by the parent via `onSelect`.
 */
export function CaptureButtonConfigModal({
  slotIndex,
  configurableEntryTypes,
  onSelect,
  onClose,
}: CaptureButtonConfigModalProps) {
  return (
    <div
      className="fixed inset-0 z-[170] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Configure Button {slotIndex + 1}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Select an expense category for this capture button:
        </p>
        <div className="grid grid-cols-2 gap-2 max-h-[50vh] overflow-y-auto">
          {configurableEntryTypes.map((t) => (
            <button
              key={t.value}
              onClick={() => onSelect(t.value)}
              className="flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2.5 text-left transition-all hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 active:scale-95"
            >
              <span className="text-xl leading-none">{t.icon}</span>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{t.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
